// The bell in the top bar: a panel of what needs a person, newest first,
// grouped Agir maintenant / Aujourd'hui, with what this computer has not seen
// yet counted on the bell and dotted in the list. A new « Agir maintenant »
// rings softly and, when the tab is in the background, shows a notification
// of this computer (once the admin allowed it). Solved notices stay a few
// hours under « Résolu ». Rules in lib/notices.ts.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LANG, S } from '../lib/strings';
import { minutesBetween, rel } from '../lib/format';
import type { Alert } from '../lib/types';
import { arrived, gone, isNew, keepResolved, noticesFrom, pruneSeen, type Notice, type Resolved } from '../lib/notices';
import { Ic } from './Icon';
import { useToast } from './Toast';

const SEEN = 'mygaz-ops-seen';
const DONE = 'mygaz-ops-resolved';
const SOUND = 'mygaz-ops-sound';
const read = <T,>(k: string, d: T): T => { try { const v = localStorage.getItem(k); return v ? (JSON.parse(v) as T) : d; } catch { return d; } };
const write = (k: string, v: unknown) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* storage off */ } };
const canNotify = typeof window !== 'undefined' && 'Notification' in window;

// A two-note chime, quiet. The browser only lets sound start after a click on
// the page, so the context is opened on the first click and reused.
let audio: AudioContext | null = null;
function wakeAudio() {
  try { audio ??= new AudioContext(); if (audio.state === 'suspended') void audio.resume(); } catch { audio = null; }
}
function chime() {
  if (!audio || audio.state !== 'running') return;
  const t0 = audio.currentTime;
  [[880, 0], [1320, 0.14]].forEach(([f, d]) => {
    const o = audio!.createOscillator(), g = audio!.createGain();
    o.type = 'sine'; o.frequency.value = f;
    g.gain.setValueAtTime(0.0001, t0 + d); g.gain.exponentialRampToValueAtTime(0.06, t0 + d + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t0 + d + 0.28);
    o.connect(g).connect(audio!.destination); o.start(t0 + d); o.stop(t0 + d + 0.3);
  });
}

interface Props {
  alerts: Alert[] | null;
  onOrder(id: string): void; onHome(id: string): void; onRemind(ids: string[]): void; onTodo(): void;
}

export function Bell({ alerts, onOrder, onHome, onRemind, onTodo }: Props) {
  const toast = useToast();
  const ns = useMemo(() => noticesFrom(alerts ?? []), [alerts]);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState<Record<string, number>>(() => pruneSeen(read(SEEN, {})));
  const [done, setDone] = useState<Resolved[]>(() => keepResolved(read(DONE, [])));
  const [sound, setSound] = useState<boolean>(() => read(SOUND, true));
  const [perm, setPerm] = useState<NotificationPermission | 'none'>(canNotify ? Notification.permission : 'none');
  const prev = useRef<{ keys: Set<string>; ns: Notice[] } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);

  const act = useCallback((n: Notice) => {
    if (n.act === 'assign' || n.act === 'order') onOrder(n.target[0]);
    else if (n.act === 'home') onHome(n.target[0]);
    else onRemind(n.target);
  }, [onOrder, onHome, onRemind]);

  const actRef = useRef(act);
  useEffect(() => { actRef.current = act; }, [act]);

  // New and solved notices, each time the live list changes.
  useEffect(() => {
    if (!alerts) return;
    const p = prev.current;
    const fresh = arrived(p?.keys ?? null, ns).filter((n) => n.level === 'now');
    const solved = p ? gone(p.ns, ns) : [];
    prev.current = { keys: new Set(ns.flatMap((n) => n.keys)), ns };
    if (solved.length) setDone((d) => { const v = keepResolved([...solved, ...d.filter((x) => !solved.some((s) => s.id === x.id))]); write(DONE, v); return v; });
    if (!fresh.length) return;
    if (sound) chime();
    const n = fresh[0];
    if (document.hidden && canNotify && Notification.permission === 'granted') {
      const note = new Notification(`MyGaz Ops · ${n.title}`, { body: fresh.length > 1 ? `${n.sub} ${S.bell.more(fresh.length - 1)}` : n.sub, tag: n.id, icon: './mygaz-icon.png' });
      note.onclick = () => { window.focus(); actRef.current(n); note.close(); };
    } else if (!document.hidden) {
      toast(`${n.title} · ${n.sub}`);
    }
  }, [alerts, ns, sound, toast]);

  const fresh = ns.filter((n) => isNew(n, seen));
  const count = fresh.length;

  // The tab's title carries the count, for a console left in a background tab.
  useEffect(() => {
    const base = 'MyGaz Ops';
    document.title = count ? `(${count}) ${base}` : base;
    return () => { document.title = base; };
  }, [count]);

  const markAll = useCallback(() => {
    setSeen((s) => { const v = { ...s }; const t = Date.now(); ns.forEach((n) => n.keys.forEach((k) => { if (!(k in v)) v[k] = t; })); write(SEEN, v); return v; });
  }, [ns]);
  const markOne = (n: Notice) => setSeen((s) => { const v = { ...s }; n.keys.forEach((k) => { v[k] = v[k] ?? Date.now(); }); write(SEEN, v); return v; });

  const close = useCallback((focusBell = true) => { setOpen(false); markAll(); if (focusBell) btn.current?.focus(); }, [markAll]);

  // Outside click and Escape close the panel; what was shown counts as seen.
  useEffect(() => {
    if (!open) return;
    const down = (e: PointerEvent) => { if (!box.current?.contains(e.target as Node)) close(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('pointerdown', down); document.removeEventListener('keydown', key, true); };
  }, [open, close]);

  useEffect(() => {
    const wake = () => wakeAudio();
    document.addEventListener('pointerdown', wake, { once: true });
    return () => document.removeEventListener('pointerdown', wake);
  }, []);

  const allow = async () => { if (!canNotify) return; try { setPerm(await Notification.requestPermission()); } catch { /* old Safari */ } };
  const toggleSound = () => { const v = !sound; setSound(v); write(SOUND, v); if (v) { wakeAudio(); setTimeout(chime, 60); } };
  const run = (n: Notice) => { markOne(n); setOpen(false); act(n); };

  const groups: [string, string, Notice[]][] = [
    ['now', S.bell.now, ns.filter((n) => n.level === 'now')],
    ['today', S.bell.today, ns.filter((n) => n.level === 'today')],
  ];
  const label = (n: Notice) => (n.act === 'assign' ? S.bell.assign : n.act === 'remind' ? S.bell.remind : S.bell.see);
  const when = (at: string | null) => (at ? rel(minutesBetween(at), LANG) : '');

  return (
    <div className="bellw" ref={box}>
      <button ref={btn} className="ibtn hide-s bellb" aria-label={S.bell.aria(count)} aria-expanded={open} aria-haspopup="dialog"
        onClick={() => { wakeAudio(); if (open) close(); else setOpen(true); }}>
        <span><Ic n="bell" s="s" /></span>
        {count > 0 && <span className="bn num">{count > 9 ? '9+' : count}</span>}
      </button>
      {open && (
        <div className="bp" role="dialog" aria-label={S.bell.title}>
          <div className="bp-h">
            <b>{S.bell.title}</b>
            {count > 0 && <button className="link" onClick={markAll}>{S.bell.markAll}</button>}
          </div>
          <div className="bp-b">
            {!ns.length && <div className="bp-empty"><b>{S.nothing}</b><span>{S.nothingSub}</span></div>}
            {groups.map(([k, title, list]) => list.length > 0 && (
              <section key={k} className={'bp-g ' + k} aria-label={title}>
                <h3>{title}</h3>
                {list.map((n) => (
                  <div key={n.id} className={'bp-i' + (isNew(n, seen) ? ' new' : '')}>
                    <i className="bp-d" aria-hidden="true" />
                    <div className="bp-t"><b>{n.title}</b><span>{n.sub}{n.at ? ` · ${when(n.at)}` : ''}</span></div>
                    <button className="btn xs" onClick={() => run(n)}>{label(n)}</button>
                  </div>
                ))}
              </section>
            ))}
            {done.length > 0 && (
              <section className="bp-g done" aria-label={S.bell.done}>
                <h3>{S.bell.done}</h3>
                {done.map((r) => (
                  <div key={r.id + r.at} className="bp-i old">
                    <i className="bp-d" aria-hidden="true" />
                    <div className="bp-t"><b>{r.title}</b><span>{r.sub} · {S.bell.solved(rel(minutesBetween(r.at), LANG))}</span></div>
                  </div>
                ))}
              </section>
            )}
          </div>
          <div className="bp-f">
            {perm === 'default' && <button className="link" onClick={allow}><Ic n="bell" s="xs" />{S.bell.allow}</button>}
            {perm === 'granted' && <span className="bp-ok"><Ic n="check" s="xs" />{S.bell.allowed}</span>}
            {perm === 'denied' && <span className="bp-no">{S.bell.blocked}</span>}
            <button className={'bp-snd' + (sound ? ' on' : '')} aria-pressed={sound} onClick={toggleSound}>{S.bell.sound}{sound ? S.bell.on : S.bell.off}</button>
            <button className="link bp-all" onClick={() => { close(false); onTodo(); }}>{S.bell.todo}<Ic n="chev" s="xs" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
